import client from './client';
import API_CONFIG from '../config';
import storage from '../utils/storage';
import Logger from '../utils/logger';

// Re-export du client pour compatibilité avec les anciens imports
export const apiClient = client;

/**
 * Récupère les informations de l'utilisateur connecté
 * @returns {Promise} Promesse contenant les données utilisateur
 */
export const fetchCurrentUser = async () => {
    const response = await apiClient.get(API_CONFIG.ENDPOINTS.AUTH.ME);
    return response.data;
};

/**
 * Connexion utilisateur
 * @param {Object} credentials - Objet contenant email et password
 * @param {string} credentials.email - Email de l'utilisateur
 * @param {string} credentials.password - Mot de passe
 * @returns {Promise} Promesse contenant les tokens et les infos utilisateur
 */
export const login = async ({ email, password }) => {
    Logger.log("Tentative de connexion avec:", { email });
    
    try {
        // Utilisation de l'instance apiClient configurée avec les intercepteurs
        const response = await apiClient.post(
            API_CONFIG.ENDPOINTS.AUTH.LOGIN,
            // Compat: certains backends exigent 'username', d'autres acceptent 'email'
            { email, username: email, password },
            {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                withCredentials: true,
                timeout: 10000 // 10 secondes de timeout
            }
        );
        
        Logger.log("Réponse du serveur reçue:", {
            status: response.status,
            data: response.data,
            headers: response.headers
        });
        
        if (response.data.access) {
            // Stocker les tokens via le service centralisé
            try {
                // ✅ Stocker le refresh token s'il est fourni
                storage.setToken(response.data.access, response.data.refresh);
                if (response.data.user) {
                    storage.setUser(response.data.user);
                }
            } catch (storageError) {
                Logger.error('Erreur lors du stockage des données:', storageError);
                throw new Error('Impossible de stocker les données de session');
            }
            
            // Mettre à jour l'instance axios avec le nouveau token
            apiClient.defaults.headers.common['Authorization'] = `Bearer ${response.data.access}`;
            
            Logger.log("Connexion réussie, utilisateur:", response.data.user);
        }
        
        return response.data;
    } catch (error) {
        const errorDetails = {
            status: error.response?.status,
            data: error.response?.data,
            message: error.message,
            config: {
                url: error.config?.url,
                method: error.config?.method,
                data: error.config?.data,
                headers: error.config?.headers
            }
        };
        
        Logger.error('Erreur lors de la connexion:', errorDetails);
        
        // Gestion des erreurs spécifiques
        if (error.code === 'ECONNABORTED') {
            throw new Error('La connexion a expiré. Veuillez réessayer.');
        } else if (!error.response) {
            throw new Error('Impossible de se connecter au serveur. Vérifiez votre connexion internet.');
        } else if (error.response.status === 400) {
            // Afficher les détails de l'erreur 400
            const errorMessage = error.response.data?.detail 
                || error.response.data?.non_field_errors?.[0]
                || Object.values(error.response.data || {}).flat().join(', ')
                || 'Requête invalide. Vérifiez vos informations.';
            throw new Error(errorMessage);
        } else if (error.response.status === 401) {
            throw new Error(error.response.data?.detail || 'Identifiants incorrects. Veuillez réessayer.');
        } else if (error.response.status === 403) {
            throw new Error('Accès refusé. Votre compte est peut-être désactivé.');
        } else if (error.response.status >= 500) {
            throw new Error('Erreur serveur. Veuillez réessayer plus tard.');
        } else {
            throw new Error(error.response.data?.detail || 'Une erreur inattendue est survenue.');
        }
    }
};

/**
 * Demande de réinitialisation de mot de passe
 * @param {string} email - Email de l'utilisateur
 * @returns {Promise}
 */
export const requestPasswordReset = async (email) => {
    try {
        const response = await apiClient.post(
            API_CONFIG.ENDPOINTS.AUTH.PASSWORD_RESET,
            { email }
        );
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.detail || 'Erreur lors de la demande de réinitialisation');
    }
};

/**
 * Confirme la réinitialisation du mot de passe
 * @param {string} uid - UID de l'utilisateur
 * @param {string} token - Token de réinitialisation
 * @param {string} newPassword - Nouveau mot de passe
 * @returns {Promise}
 */
export const confirmPasswordReset = async (uid, token, newPassword) => {
    try {
        const response = await apiClient.post(
            API_CONFIG.ENDPOINTS.AUTH.PASSWORD_RESET_CONFIRM,
            { uid, token, new_password: newPassword }
        );
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.detail || 'Erreur lors de la réinitialisation du mot de passe');
    }
};

/**
 * Inscription d'un nouvel utilisateur ADMIN
 * @param {Object} userData - Données de l'utilisateur
 * @returns {Promise}
 */
export const registerAdmin = async (userData) => {
    try {
        const response = await apiClient.post(
            API_CONFIG.ENDPOINTS.AUTH.REGISTER_ADMIN || '/api/accounts/register-admin/',
            userData
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Activation du compte via token email
 * @param {string} token - Token d'activation
 * @returns {Promise}
 * @throws {Error} Si le token est invalide ou déjà utilisé
 */
export const activateAccount = async (token) => {
    if (!token || typeof token !== 'string') {
        throw new Error('Token invalide');
    }

    try {
        const response = await apiClient.get(
            `/api/accounts/activate/?token=${encodeURIComponent(token)}`
        );
        return response.data;
    } catch (error) {
        // Extraire le message d'erreur de manière cohérente
        let errorMessage = 'Erreur lors de l\'activation du compte';

        if (error.response?.data?.token) {
            // Format: {"token": ["message"]}
            if (Array.isArray(error.response.data.token)) {
                errorMessage = error.response.data.token[0];
            } else if (typeof error.response.data.token === 'string') {
                errorMessage = error.response.data.token;
            }
        } else if (error.response?.data?.detail) {
            errorMessage = error.response.data.detail;
        } else if (error.response?.data?.message) {
            errorMessage = error.response.data.message;
        } else if (error.message) {
            errorMessage = error.message;
        }

        const customError = new Error(errorMessage);
        customError.response = error.response;
        customError.status = error.response?.status;
        throw customError;
    }
};

/**
 * Inscription d'un nouvel utilisateur
 * @param {Object} userData - Données de l'utilisateur
 * @returns {Promise}
 */
export const register = async (userData) => {
    try {
        const response = await apiClient.post(
            API_CONFIG.ENDPOINTS.AUTH.REGISTER,
            userData
        );
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.detail || 'Erreur lors de l\'inscription');
    }
};

/**
 * Connexion SSO Easy Suite
 * @param {string} token - Token Easy Suite
 * @returns {Promise} Promesse contenant les tokens et les infos utilisateur
 */
export const loginSSO = async (token) => {
    Logger.log("Tentative de connexion SSO avec token");
    
    try {
        const response = await apiClient.post(
            API_CONFIG.ENDPOINTS.AUTH.SSO_VERIFY_AND_LOGIN,
            { token },
            {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                withCredentials: true,
                timeout: 10000
            }
        );
        
        Logger.log("Réponse SSO reçue:", {
            status: response.status,
            data: response.data
        });
        
        if (response.data.success && response.data.token) {
            // ✅ Utiliser storage.setToken() pour une gestion cohérente
            try {
                storage.setToken(response.data.token, response.data.refresh);
                
                // Stocker l'utilisateur avec son rôle
                if (response.data.user) {
                    storage.setUser(response.data.user);
                }
                
                Logger.log('✅ Token et utilisateur stockés avec succès');
            } catch (storageError) {
                Logger.error('Erreur lors du stockage des données SSO:', storageError);
                throw new Error('Impossible de stocker les données de session');
            }
            
            // Mettre à jour l'instance axios avec le nouveau token
            apiClient.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
            
            Logger.log("Connexion SSO réussie, utilisateur:", response.data.user);
        }
        
        return response.data;
    } catch (error) {
        const errorDetails = {
            status: error.response?.status,
            data: error.response?.data,
            message: error.message
        };
        
        Logger.error('Erreur lors de la connexion SSO:', errorDetails);
        
        if (error.code === 'ECONNABORTED') {
            throw new Error('La connexion a expiré. Veuillez réessayer.');
        } else if (!error.response) {
            throw new Error('Impossible de se connecter au serveur. Vérifiez votre connexion internet.');
        } else if (error.response.status >= 500) {
            throw new Error('Erreur serveur. Veuillez réessayer plus tard.');
        } else {
            throw new Error(error.response.data?.message || 'Une erreur inattendue est survenue.');
        }
    }
};

/**
 * Déconnexion de l'utilisateur
 * @returns {Promise}
 */
export const logout = async () => {
    try {
        storage.clearAll();
        return { success: true };
    } catch (error) {
        Logger.error('Erreur lors de la déconnexion:', error);
        throw error;
    }
};
